/**
 * Pick who a Swap moves in, and which of their seats on another Pod they leave.
 *
 * One row per seat: someone on two other Pods appears twice, because the request ends
 * exactly one of those seats. Served by the narrow swap-candidates endpoint — name,
 * Discipline and seat only — so it is safe for [ACCT], like ResourceOptionPicker.
 */

import { X } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { useSwapCandidates } from "@/hooks/use-staffing-requests";
import type { StaffingSwapCandidate, SwapSeatOption } from "@/schemas/staffing-requests";

export interface SwapChoice {
  candidate: StaffingSwapCandidate;
  seat: SwapSeatOption;
}

interface Props {
  solutionId: string;
  selected: SwapChoice | null;
  onChange: (choice: SwapChoice | null) => void;
  id?: string;
}

function disciplineCodes(candidate: StaffingSwapCandidate): string {
  return candidate.disciplines.map((d) => d.code).join(" · ");
}

function seatText(seat: SwapSeatOption): string {
  return `${seat.solution.name} · ${seat.role}`;
}

export function SwapCandidatePicker({ solutionId, selected, onChange, id }: Props) {
  const [search, setSearch] = useState("");
  const debounced = useDebouncedValue(search, 250);
  const { data, isLoading, isError, refetch } = useSwapCandidates(
    selected ? null : solutionId,
    debounced,
  );

  if (selected) {
    return (
      <div className="flex h-9 items-center justify-between gap-2 rounded-md border border-input bg-card px-3 text-sm">
        <span className="truncate">
          <span className="font-medium">{selected.candidate.name}</span>
          <span className="ml-2 text-xs text-muted-foreground">
            leaves {seatText(selected.seat)}
          </span>
        </span>
        <button
          type="button"
          onClick={() => onChange(null)}
          aria-label={`Clear ${selected.candidate.name}`}
          className="rounded p-0.5 text-muted-foreground transition-colors hover:text-foreground"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  }

  const rows = (data ?? []).flatMap((candidate) =>
    candidate.seats.map((seat) => ({ candidate, seat })),
  );

  return (
    <div className="space-y-1.5">
      <Input
        id={id}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search Resources on other Pods"
        autoComplete="off"
      />
      <div className="max-h-48 overflow-y-auto rounded-md border border-border bg-card">
        {isLoading ? (
          <p className="px-3 py-2 text-sm text-muted-foreground">Loading…</p>
        ) : isError ? (
          <div className="flex items-center justify-between px-3 py-2 text-sm text-muted-foreground">
            Could not load Resources.
            <Button type="button" variant="ghost" size="sm" onClick={() => refetch()}>
              Retry
            </Button>
          </div>
        ) : rows.length === 0 ? (
          <p className="px-3 py-2 text-sm text-muted-foreground">Nobody on another Pod matches.</p>
        ) : (
          <ul aria-label="Resources on other Pods">
            {rows.map(({ candidate, seat }) => (
              <li key={seat.assignmentId}>
                <button
                  type="button"
                  disabled={seat.pendingMove}
                  onClick={() => onChange({ candidate, seat })}
                  className="flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-sm transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <span className="min-w-0 truncate">
                    <span className="font-medium">{candidate.name}</span>
                    <span className="ml-2 text-xs text-muted-foreground">
                      {seatText(seat)}
                      {seat.pendingMove && " — move already requested"}
                    </span>
                  </span>
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {disciplineCodes(candidate)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
